/** SUNTRONIX ONE — Field Job detail / execution (mobile-first). */
import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  X, Navigation, LogIn, Play, Pause, CheckCircle2, LogOut, Package, Trash2,
  Sparkles, FileText, PenTool, AlertTriangle,
} from "lucide-react";

const din = "mt-1 w-full text-[12px] border border-slate-200 rounded-md px-2 py-1.5";

const ACTIONS = [
  { action: "EN_ROUTE", label: "En route", icon: Navigation, show: ["SCHEDULED", "ASSIGNED"] },
  { action: "CHECK_IN", label: "Check in", icon: LogIn, show: ["SCHEDULED", "ASSIGNED", "EN_ROUTE"] },
  { action: "START", label: "Start", icon: Play, show: ["CHECKED_IN", "PAUSED"] },
  { action: "PAUSE", label: "Pause", icon: Pause, show: ["IN_PROGRESS"] },
  { action: "COMPLETE", label: "Complete", icon: CheckCircle2, show: ["IN_PROGRESS", "PAUSED", "CHECKED_IN"] },
  { action: "CHECK_OUT", label: "Check out", icon: LogOut, show: ["COMPLETED", "SIGNED_OFF"] },
];

function SignaturePad({ onChange }) {
  const ref = useRef(null);
  const drawing = useRef(false);
  const pos = (e, c) => {
    const r = c.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: t.clientX - r.left, y: t.clientY - r.top };
  };
  const start = (e) => { drawing.current = true; draw(e); };
  const end = () => {
    drawing.current = false;
    const c = ref.current; if (c) onChange(c.toDataURL("image/png"));
  };
  const draw = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const c = ref.current; const ctx = c.getContext("2d");
    const p = pos(e, c);
    ctx.lineWidth = 2; ctx.lineCap = "round"; ctx.strokeStyle = "#0f172a";
    ctx.lineTo(p.x, p.y); ctx.stroke(); ctx.beginPath(); ctx.moveTo(p.x, p.y);
  };
  const clear = () => {
    const c = ref.current; c.getContext("2d").clearRect(0, 0, c.width, c.height);
    onChange("");
  };
  return (
    <div>
      <canvas ref={ref} width={320} height={120} data-testid="fs-signature-pad"
        onMouseDown={start} onMouseMove={draw} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={draw} onTouchEnd={end}
        className="w-full touch-none rounded-md border border-slate-300 bg-white" />
      <button onClick={clear} type="button" className="mt-1 text-[10px] text-slate-500 underline">Clear signature</button>
    </div>
  );
}

export function JobDetail({ jobId, meta, onClose, onChange }) {
  const [job, setJob] = useState(null);
  const [parts, setParts] = useState([]);
  const [tab, setTab] = useState("work");
  const [comp, setComp] = useState({ work_done: "", diagnosis: "", resolution: "", engineer_notes: "", asset_condition: "" });
  const [part, setPart] = useState({ name: "", sku: "", quantity: 1 });
  const [sign, setSign] = useState({ customer_name: "", signature: "", comments: "" });
  const [ai, setAi] = useState({ mode: null, text: "", loading: false });

  const load = useCallback(() => {
    api.get(`/field-service/jobs/${jobId}`).then(({ data }) => {
      setJob(data.job); setParts(data.parts || []);
      if (data.job?.completion) setComp((p) => ({ ...p, ...data.job.completion }));
    }).catch(() => {});
  }, [jobId]);
  useEffect(() => { load(); }, [load]);

  const doAction = async (action) => {
    try {
      let payload = { action };
      if (action === "CHECK_IN" && navigator.geolocation) {
        payload = await new Promise((res) => {
          navigator.geolocation.getCurrentPosition(
            (p) => res({ action, lat: p.coords.latitude, lng: p.coords.longitude }),
            () => res({ action }), { timeout: 4000 });
        });
      }
      await api.post(`/field-service/jobs/${jobId}/transition`, payload);
      toast.success(action.replace(/_/g, " ")); load(); onChange?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const saveCompletion = async () => {
    if (!comp.work_done.trim()) return toast.error("Work done is required");
    try { await api.post(`/field-service/jobs/${jobId}/complete`, comp);
      toast.success("Completion saved"); load(); onChange?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const addPart = async () => {
    if (!part.name.trim()) return;
    try {
      const { data } = await api.post(`/field-service/jobs/${jobId}/parts`, { ...part, quantity: Number(part.quantity) || 1 });
      toast.success(data.inventory_deducted ? "Part consumed (stock deducted)" : "Part recorded");
      setPart({ name: "", sku: "", quantity: 1 }); load();
    } catch (e) { toast.error("Failed"); }
  };
  const delPart = async (id) => {
    try { await api.delete(`/field-service/jobs/${jobId}/parts/${id}`); load(); } catch { /* noop */ }
  };

  const doSignOff = async () => {
    if (!sign.customer_name.trim() || !sign.signature) return toast.error("Name & signature required");
    try { await api.post(`/field-service/jobs/${jobId}/sign-off`, sign);
      toast.success("Customer signed off"); load(); onChange?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const runAi = async (mode) => {
    setAi({ mode, text: "", loading: true });
    try { const { data } = await api.post(`/field-service/jobs/${jobId}/ai`, { mode });
      setAi({ mode, text: data.result, loading: false });
    } catch (e) { setAi({ mode: null, text: "", loading: false }); toast.error(e.response?.data?.detail || "AI failed"); }
  };
  const useAi = () => {
    if (ai.mode === "draft_completion") setComp((p) => ({ ...p, work_done: ai.text }));
    setAi({ mode: null, text: "", loading: false });
    toast.success("Applied to work notes");
  };

  if (!job) return null;
  const canDispatch = meta?.can_dispatch;

  return (
    <div className="fixed inset-0 z-[120] flex items-start justify-center bg-slate-900/50 p-0 sm:p-4 sm:pt-8" onClick={onClose}>
      <div className="flex h-full w-full max-w-xl flex-col overflow-hidden bg-white sm:h-auto sm:max-h-[92vh] sm:rounded-2xl shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="fs-job-detail">
        {/* Header */}
        <div className="flex items-center gap-2 bg-teal-900 px-4 py-3 text-white">
          <div className="min-w-0 flex-1">
            <div className="font-mono text-[10px] text-teal-200">{job.job_number} · {job.job_type}</div>
            <div className="truncate text-[14px] font-semibold">{job.customer_name || "—"}</div>
          </div>
          <span className="rounded bg-white/15 px-2 py-0.5 text-[10px] font-semibold">{job.status}</span>
          <button onClick={onClose} data-testid="fs-detail-close"><X className="h-4 w-4" /></button>
        </div>

        {/* Info */}
        <div className="border-b border-slate-100 px-4 py-2 text-[11px] text-slate-600">
          <div className="flex flex-wrap gap-x-4 gap-y-0.5">
            {job.contact_name && <span>👤 {job.contact_name} {job.contact_phone}</span>}
            {job.location && <span>📍 {job.location}</span>}
            {job.serial && <span>🔧 <span className="font-mono">{job.serial}</span></span>}
            {job.ticket_number && <span>🎫 {job.ticket_number}</span>}
            {job.scheduled_at && <span>🕒 {(job.scheduled_at || "").slice(0, 16).replace("T", " ")}</span>}
          </div>
          {job.instructions && <div className="mt-1 rounded bg-slate-50 px-2 py-1 text-[11px] text-slate-700">{job.instructions}</div>}
        </div>

        {/* Action bar */}
        <div className="flex flex-wrap gap-1.5 border-b border-slate-100 px-3 py-2">
          {ACTIONS.filter((a) => a.show.includes(job.status)).map((a) => {
            const Icon = a.icon;
            return (
              <button key={a.action} onClick={() => doAction(a.action)} data-testid={`fs-act-${a.action.toLowerCase()}`}
                className="inline-flex items-center gap-1 rounded-md bg-teal-800 px-2.5 py-1.5 text-[11px] font-semibold text-white">
                <Icon className="h-3.5 w-3.5" /> {a.label}
              </button>
            );
          })}
          {canDispatch && !job.escalated && (
            <button onClick={async () => { await api.patch(`/field-service/jobs/${jobId}`, { escalated: true, escalation_reason: "Escalated from field" }); load(); onChange?.(); }}
              data-testid="fs-escalate" className="inline-flex items-center gap-1 rounded-md border border-rose-300 px-2.5 py-1.5 text-[11px] font-semibold text-rose-700">
              <AlertTriangle className="h-3.5 w-3.5" /> Escalate
            </button>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-slate-100 px-3 pt-2 text-[11px]">
          {[["work", "Work"], ["parts", `Parts (${parts.length})`], ["signoff", "Sign-off"], ["report", "Report"]].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`fs-tab-${k}`}
              className={`rounded-t px-3 py-1.5 font-semibold ${tab === k ? "bg-slate-100 text-slate-900" : "text-slate-500"}`}>{l}</button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {tab === "work" && (
            <div className="space-y-2" data-testid="fs-work-tab">
              <div className="flex flex-wrap gap-1.5">
                {[["summarise_issue", "Summarise issue"], ["suggest_checklist", "Checklist"], ["draft_completion", "Draft notes"], ["missing_evidence", "Missing evidence"]].map(([m, l]) => (
                  <button key={m} onClick={() => runAi(m)} data-testid={`fs-ai-${m}`}
                    className="inline-flex items-center gap-1 rounded-md border border-violet-200 px-2 py-1 text-[10px] font-semibold text-violet-700 hover:bg-violet-50">
                    <Sparkles className="h-3 w-3" /> {l}
                  </button>
                ))}
              </div>
              {(ai.loading || ai.text) && (
                <div className="rounded-md border border-violet-200 bg-violet-50 p-2.5 text-[11px] text-slate-700 whitespace-pre-wrap" data-testid="fs-ai-result">
                  {ai.loading ? "Thinking…" : ai.text}
                  {!ai.loading && ai.mode === "draft_completion" && (
                    <button onClick={useAi} className="mt-1 block rounded bg-teal-800 px-2 py-1 text-[10px] font-semibold text-white">Use as work notes</button>
                  )}
                </div>
              )}
              <label className="block text-[10px] text-slate-500">Work done *
                <textarea value={comp.work_done} onChange={(e) => setComp({ ...comp, work_done: e.target.value })} rows={3} className={din} data-testid="fs-work-done" /></label>
              <div className="grid grid-cols-2 gap-2">
                <label className="text-[10px] text-slate-500">Diagnosis
                  <textarea value={comp.diagnosis} onChange={(e) => setComp({ ...comp, diagnosis: e.target.value })} rows={2} className={din} /></label>
                <label className="text-[10px] text-slate-500">Resolution
                  <textarea value={comp.resolution} onChange={(e) => setComp({ ...comp, resolution: e.target.value })} rows={2} className={din} /></label>
              </div>
              <label className="block text-[10px] text-slate-500">Engineer notes
                <textarea value={comp.engineer_notes} onChange={(e) => setComp({ ...comp, engineer_notes: e.target.value })} rows={2} className={din} /></label>
              <button onClick={saveCompletion} data-testid="fs-save-completion"
                className="w-full rounded-md bg-teal-800 py-2 text-[12px] font-semibold text-white">Save completion</button>
            </div>
          )}

          {tab === "parts" && (
            <div className="space-y-2" data-testid="fs-parts-tab">
              {parts.map((p) => (
                <div key={p.id} className="flex items-center gap-2 rounded-md border border-slate-200 px-2.5 py-1.5 text-[11px]" data-testid={`fs-part-${p.id}`}>
                  <Package className="h-3.5 w-3.5 text-slate-400" />
                  <span className="font-semibold">{p.quantity}× {p.name}</span>
                  {p.sku && <span className="font-mono text-[9.5px] text-slate-400">{p.sku}</span>}
                  <span className={`rounded px-1.5 py-0.5 text-[9px] ${p.inventory_deducted ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600"}`}>
                    {p.inventory_deducted ? "stock deducted" : "recorded"}
                  </span>
                  <button onClick={() => delPart(p.id)} className="ml-auto text-rose-500"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              ))}
              {!parts.length && <div className="text-[11px] text-slate-400">No parts consumed.</div>}
              <div className="grid grid-cols-4 gap-1.5 pt-1">
                <input placeholder="Part name" value={part.name} onChange={(e) => setPart({ ...part, name: e.target.value })} className="col-span-2 rounded-md border border-slate-200 px-2 py-1.5 text-[11px]" data-testid="fs-part-name" />
                <input placeholder="SKU" value={part.sku} onChange={(e) => setPart({ ...part, sku: e.target.value })} className="rounded-md border border-slate-200 px-2 py-1.5 text-[11px] font-mono" />
                <input type="number" min={1} value={part.quantity} onChange={(e) => setPart({ ...part, quantity: e.target.value })} className="rounded-md border border-slate-200 px-2 py-1.5 text-[11px]" />
              </div>
              <button onClick={addPart} data-testid="fs-add-part" className="w-full rounded-md border border-teal-700 py-1.5 text-[11px] font-semibold text-teal-800">+ Consume part</button>
              <div className="text-[9.5px] text-slate-400">Parts are linked to the job, asset &amp; ticket. Stock is deducted only once per line.</div>
            </div>
          )}

          {tab === "signoff" && (
            <div className="space-y-2" data-testid="fs-signoff-tab">
              {job.signoff ? (
                <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-[12px]">
                  <div className="font-semibold text-emerald-800">Signed off ✓</div>
                  <div className="text-slate-700">By {job.signoff.customer_name} · {(job.signoff.signed_at || "").slice(0, 16).replace("T", " ")}</div>
                  {job.signoff.comments && <div className="mt-1 text-slate-600">"{job.signoff.comments}"</div>}
                  {job.signoff.signature && <img src={job.signoff.signature} alt="signature" className="mt-2 h-20 rounded border border-slate-200 bg-white" />}
                </div>
              ) : !job.completion ? (
                <div className="rounded-md bg-amber-50 px-3 py-2 text-[11px] text-amber-800">Complete the work first, then capture customer sign-off.</div>
              ) : (
                <>
                  <div className="flex items-center gap-1 text-[11px] font-semibold text-slate-700"><PenTool className="h-3.5 w-3.5" /> Customer signature</div>
                  <SignaturePad onChange={(d) => setSign((p) => ({ ...p, signature: d }))} />
                  <input placeholder="Customer name *" value={sign.customer_name} onChange={(e) => setSign({ ...sign, customer_name: e.target.value })} className={din} data-testid="fs-signoff-name" />
                  <textarea placeholder="Comments" value={sign.comments} onChange={(e) => setSign({ ...sign, comments: e.target.value })} rows={2} className={din} />
                  <button onClick={doSignOff} data-testid="fs-signoff-submit" className="w-full rounded-md bg-teal-800 py-2 text-[12px] font-semibold text-white">Capture sign-off &amp; create report</button>
                </>
              )}
            </div>
          )}

          {tab === "report" && <ReportTab jobId={jobId} />}
        </div>
      </div>
    </div>
  );
}

function ReportTab({ jobId }) {
  const [rep, setRep] = useState(null);
  useEffect(() => { api.get(`/field-service/jobs/${jobId}/report`).then(({ data }) => setRep(data.report)).catch(() => {}); }, [jobId]);
  if (!rep) return <div className="text-[11px] text-slate-400">Loading…</div>;
  const Row = ({ k, v }) => (
    <div className="flex justify-between border-b border-slate-100 py-1 text-[11px]"><span className="text-slate-500">{k}</span><span className="font-semibold text-slate-800">{v || "—"}</span></div>
  );
  return (
    <div className="space-y-1" data-testid="fs-report-tab">
      <div className="mb-1 flex items-center gap-1 text-[12px] font-semibold"><FileText className="h-4 w-4 text-teal-700" /> Completion Report</div>
      <Row k="Job" v={`${rep.job_number} · ${rep.job_type}`} />
      <Row k="Customer" v={rep.customer} />
      <Row k="Engineer" v={rep.engineer} />
      <Row k="Asset" v={rep.asset} />
      <Row k="On-site (min)" v={rep.on_site_minutes} />
      <Row k="Work done" v={rep.completion?.work_done} />
      <Row k="Diagnosis" v={rep.completion?.diagnosis} />
      <Row k="Resolution" v={rep.completion?.resolution} />
      <Row k="Parts" v={(rep.parts || []).map((p) => `${p.quantity}× ${p.name}`).join(", ")} />
      <Row k="Signed by" v={rep.signoff?.customer_name} />
    </div>
  );
}

export default JobDetail;
